export enum Locale {
	EN = "en",
	/**
	 * Armenian.
	 *
	 * The value is the BCP 47 tag `hy`, and it is also the URL segment
	 * (`/hy/about/`). It used to be `am` — the ISO 639-1 code for *Amharic* — so
	 * the URL claimed one language while `hreflang` claimed another. `hy` is
	 * correct in both roles, which is why they are now the same string.
	 * `/am/*` still 301s to `/hy/*` in `nginx.conf`.
	 */
	HY = "hy",
}

export const SUPPORTED_LANGUAGES = Object.values(Locale);

/**
 * The locale served WITHOUT a URL prefix.
 *
 * English lives at the root (`/about/`); Armenian is prefixed (`/hy/about/`).
 */
export const DEFAULT_LANGUAGE = Locale.EN;

/**
 * Locale used when a translation key is missing in the active language.
 *
 * Deliberately NOT the same as {@link DEFAULT_LANGUAGE}: English is the source
 * language the copy is authored in, so an untranslated key falls back to
 * readable English rather than to a raw key.
 */
export const FALLBACK_LANGUAGE = Locale.EN;

/**
 * The locale `hreflang="x-default"` points at.
 *
 * x-default is what Google shows a user whose language we do not target —
 * English, which is also what the unprefixed root serves.
 */
export const X_DEFAULT_LANGUAGE = Locale.EN;

/**
 * URL path segment → BCP 47 language tag.
 *
 * Identity today, because the segments ARE the tags — see {@link Locale.HY}.
 * The indirection stays: it is the single place to look when a segment and a
 * language tag need to diverge again, and every hreflang / `<html lang>` /
 * og:locale in the app already reads through it.
 */
export const HREFLANG_BY_LOCALE: Record<Locale, string> = {
	[Locale.EN]: "en",
	[Locale.HY]: "hy",
};

/** Open Graph locale (underscore form), keyed by URL path segment. */
export const OG_LOCALE_BY_LOCALE: Record<Locale, string> = {
	[Locale.EN]: "en_US",
	[Locale.HY]: "hy_AM",
};

export const LANGUAGE_CONFIG = {
	[Locale.EN]: {
		name: "English",
		nativeName: "English",
		flag: "us",
		dir: "ltr",
		currency: "USD",
		dateFormat: "MM/dd/yyyy",
		timeFormat: "12h",
	},
	[Locale.HY]: {
		name: "Armenian",
		nativeName: "Հայերեն",
		flag: "am",
		dir: "ltr",
		currency: "AMD",
		dateFormat: "dd.MM.yyyy",
		timeFormat: "24h",
	},
} as const;

// The catalogs are deliberately NOT imported here.
//
// This module is reachable from client code, so importing the JSON would bundle
// every string of every language into the browser payload — ~250 KB, of which a
// visitor needs one language and one page's worth. They now live in
// `catalogs.server.ts` (server/prerender only) and reach the client as a
// per-page slice on `pageContext.translations`.

// Utility function
export function isValidLocale(locale: string): locale is Locale {
	return SUPPORTED_LANGUAGES.includes(locale as Locale);
}
